import { useState } from 'react';
import { Brain, Check, Pencil, Target, Trash2, X } from 'lucide-react';
import { Sheet } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { agentApi, AgentMemory } from '../../lib/agentApi';

/**
 * "What Ava knows": everything in the user's memory, editable and deletable.
 * Unanswered topics offer an "Ask me" shortcut that starts that question in chat.
 */
export function MemorySheet({
  open,
  onClose,
  memory,
  onChange,
  onAsk
}: {
  open: boolean;
  onClose: () => void;
  memory: AgentMemory | null;
  onChange: (memory: AgentMemory) => void;
  onAsk: (topicLabel: string) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answered = memory?.topics.filter(t => t.answer).length ?? 0;
  const total = memory?.topics.length ?? 0;

  async function run(fn: () => Promise<AgentMemory>) {
    setBusy(true);
    setError(null);
    try {
      onChange(await fn());
      setEditing(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<span className="flex items-center gap-2"><Brain className="h-4 w-4 text-brand" /> What Ava knows</span>}
      description="Ava uses this to tailor her help. Edit or remove anything, any time."
      footer={
        confirmReset ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300">Forget everything? Chats are kept.</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmReset(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" disabled={busy} onClick={() => run(agentApi.forgetEverything).then(() => setConfirmReset(false))}>Forget all</Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 dark:text-red-400" onClick={() => setConfirmReset(true)} disabled={!answered && !memory?.notes.length && !memory?.goal}>
            <Trash2 className="h-3.5 w-3.5" /> Forget everything
          </Button>
        )
      }
    >
      {!memory ? null : (
        <div className="space-y-6">
          <div>
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
              <span>Profile</span>
              <span>{answered} of {total}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-neutral-800">
              <div className="h-full rounded-full bg-gradient-to-r from-brand to-accent transition-all duration-500" style={{ width: `${total ? (answered / total) * 100 : 0}%` }} />
            </div>
          </div>

          {memory.goal && (
            <div className="rounded-2xl border border-brand/20 bg-brand-muted/60 p-4 dark:border-brand/30 dark:bg-brand/10">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-brand-dark dark:text-brand-light">
                <Target className="h-3.5 w-3.5" /> Primary goal
              </div>
              <p className="mt-1 font-medium text-gray-900 dark:text-white">{memory.goal.label}</p>
              {memory.goal.rationale && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{memory.goal.rationale}</p>}
            </div>
          )}

          <ul className="space-y-3">
            {memory.topics.map(t => (
              <li key={t.id} className="group rounded-xl border border-gray-100 p-3 dark:border-neutral-800">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</span>
                  {t.answer && editing !== t.id && (
                    <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <button className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-neutral-800" aria-label={`Edit ${t.label}`} onClick={() => { setEditing(t.id); setDraft(t.answer!); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" aria-label={`Forget ${t.label}`} disabled={busy} onClick={() => run(() => agentApi.forgetProfileAnswer(t.id))}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {editing === t.id ? (
                  <div className="mt-2 space-y-2">
                    <Textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} autoFocus />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5" /> Cancel</Button>
                      <Button size="sm" disabled={busy || !draft.trim()} onClick={() => run(() => agentApi.updateProfileAnswer(t.id, draft))}><Check className="h-3.5 w-3.5" /> Save</Button>
                    </div>
                  </div>
                ) : t.answer ? (
                  <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{t.answer}</p>
                ) : (
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-sm text-gray-400 dark:text-gray-500">Not shared yet</span>
                    <button className="text-xs font-medium text-brand hover:underline dark:text-brand-light" onClick={() => onAsk(t.label)}>Tell Ava</button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {memory.notes.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Other notes</div>
              <ul className="space-y-2">
                {memory.notes.map(n => (
                  <li key={n.id} className="group flex items-start justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:bg-neutral-800/60 dark:text-gray-200">
                    <span>{n.text}</span>
                    <button className="rounded-md p-1 text-gray-400 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100" aria-label="Forget note" disabled={busy} onClick={() => run(() => agentApi.forgetNote(n.id))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {memory.interviewStatus === 'complete' && (
            <Badge variant="secondary"><Check className="h-3 w-3" /> Profile complete</Badge>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </Sheet>
  );
}
